import React, { useCallback, useEffect, useState } from "react";
import {
  faChevronLeft,
  faChevronRight,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { Button, Col, Row } from "reactstrap";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { pdfjs, Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const LOAD_ERROR = "This document could not be loaded.";

const JobInvoiceAttachment = ({ attachmentUrl, onRefreshUrl }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);

  // The document is served through a presigned URL that expires, so the one
  // that came with the payload is kept in state and refreshed on demand
  // instead of forcing the user to reload the screen.
  const [url, setUrl] = useState(attachmentUrl);
  const [hasRefreshed, setHasRefreshed] = useState(false);
  const [error, setError] = useState();

  useEffect(() => {
    setUrl(attachmentUrl);
    setHasRefreshed(false);
    setError();
  }, [attachmentUrl]);

  const refreshUrl = useCallback(async () => {
    if (!onRefreshUrl) {
      return null;
    }
    try {
      return await onRefreshUrl();
    } catch (err) {
      return null;
    }
  }, [onRefreshUrl]);

  const onLoadError = async () => {
    // Once per URL only: a permanent failure - a missing object, a bucket
    // without CORS - would otherwise keep asking for new URLs forever.
    if (hasRefreshed) {
      return setError(LOAD_ERROR);
    }
    setHasRefreshed(true);
    const refreshedUrl = await refreshUrl();
    if (!refreshedUrl) {
      return setError(LOAD_ERROR);
    }
    setUrl(refreshedUrl);
  };

  const onDownload = async () => {
    try {
      // The URL in state can be older than its lifetime, so a fresh one is
      // requested before opening the tab.
      window.open((await refreshUrl()) || url, "new");
    } catch (err) {}
  };

  return (
    <Col className="col-12">
      <div className="d-flex justify-content-end">
        <Button
          color="tertiary"
          size="sm"
          className="rounded"
          onClick={() => onDownload()}
        >
          <span className="font-weight-bold">PDF</span>
          <FontAwesomeIcon icon={faDownload} className="ml-1" />
        </Button>
      </div>
      <Row>
        <Col className="flex-shrink-0 py-3">
          {!url ? (
            <div className="text-center text-muted mt-4">
              No data has been recorded
            </div>
          ) : error ? (
            <div className="text-center text-muted mt-4">{error}</div>
          ) : (
            <>
              <Document
                className="d-flex justify-content-center"
                file={{
                  url,
                }}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={onLoadError}
              >
                <Page className="border rounded" pageNumber={pageNumber} />
              </Document>
              <div className="d-flex justify-content-between p-2 align-items-center text-body bg-white border mt-4 mx-1 rounded">
                <Button
                  className="rounded"
                  onClick={() => setPageNumber(Math.max(pageNumber - 1, 1))}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                <span>
                  Page {pageNumber} of {numPages}
                </span>
                <Button
                  className="rounded"
                  onClick={() =>
                    setPageNumber(Math.min(pageNumber + 1, numPages))
                  }
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </>
          )}
        </Col>
      </Row>
    </Col>
  );
};

export default JobInvoiceAttachment;
